// A row that scrolls sideways inside its own box (docs/plans/mobile-fixes.md): it never widens
// the page, fades at a side where there is more to see (`data-more-start` / `data-more-end`,
// drawn by the `scroll-more` utility in styles.css), and keeps the chosen item in view.
import { useLayoutEffect, type RefObject } from 'react'

export function useScrollMore(ref: RefObject<HTMLElement | null>, chosen?: unknown): void {
  useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const mark = () => {
      const start = el.scrollLeft > 1
      const end = el.scrollLeft + el.clientWidth < el.scrollWidth - 1
      el.toggleAttribute('data-more-start', start)
      el.toggleAttribute('data-more-end', end)
    }
    mark()
    el.addEventListener('scroll', mark, { passive: true })
    const resized = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(mark)
    resized?.observe(el)
    if (el.firstElementChild) resized?.observe(el.firstElementChild)
    return () => {
      el.removeEventListener('scroll', mark)
      resized?.disconnect()
    }
  }, [ref])

  // The chosen item, moved into view when the choice changes (a filter picked from a link).
  useLayoutEffect(() => {
    const el = ref.current
    const item = el?.querySelector<HTMLElement>('[aria-selected="true"]')
    if (!el || !item || el.scrollWidth <= el.clientWidth) return
    const box = el.getBoundingClientRect()
    const r = item.getBoundingClientRect()
    if (r.left < box.left) el.scrollLeft -= box.left - r.left + 16
    else if (r.right > box.right) el.scrollLeft += r.right - box.right + 16
  }, [ref, chosen])
}
